import { useEffect, useRef, useState } from "react";
import type {
  CoworldReplayFrameProps,
  CoworldReplayFrameState,
} from "./replay-frame.types";

export function useReplayFrameControls({
  chrome = "full",
  onStateChange,
}: Pick<CoworldReplayFrameProps, "chrome" | "onStateChange">) {
  const [playing, setPlaying] = useState(chrome !== "controls");
  const [attempt, setAttempt] = useState(0);
  const gate = useRef<HTMLOutputElement>(null);
  const [frameState, setFrameState] =
    useState<CoworldReplayFrameState>("starting");
  const reportState = useRef(onStateChange);
  useEffect(() => {
    reportState.current = onStateChange;
  }, [onStateChange]);
  useEffect(() => {
    reportState.current?.(frameState);
  }, [frameState]);

  useEffect(() => {
    if (chrome !== "controls") return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const connection =
      "connection" in navigator ? navigator.connection : undefined;
    const saveData =
      typeof connection === "object" &&
      connection !== null &&
      "saveData" in connection &&
      connection.saveData === true;

    if (reducedMotion || saveData) return;

    const target = gate.current;

    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        setPlaying(true);
      },
      { threshold: 0.2 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [chrome]);

  return {
    playing,
    attempt,
    gate,
    reportState: setFrameState,
    play: () => setPlaying(true),
    pause: () => setPlaying(false),
    retry: () => setAttempt((value) => value + 1),
  };
}
